<template>
  <div class="wrapper-page">
    <loading v-if="loading" class="centered"/>
    <div v-else>
      <div v-if="ready" class="app-login invitation-login">
        <section class="internal">
          <form class="new_session">
            <div v-if="outlookSupported" class="form-group text-center">
              <button
                      type="button"
                      data-disable-with="Log In"
                      class="btn btn-primary login-btn"
                      @click.stop="openLogin()"
              >{{ $t('login.header') }}</button>
            </div>
            <div v-else class="form-group text-center">
              <h5>{{ $t('msoffice.addin_not_supported') }}</h5>
                <div v-if="isPersonalised">
                  <p>{{ $t('msoffice.host_not_allowed', {host: ancestor.host}) }}</p>
                  <a @click="openNewMessage()">{{ $t('msoffice.request_allow_host') }}</a>
                </div>
            </div>
            <div class="form-group" v-show="outlookSupported">
              <div class="col-12 text-center">
                <router-link :to="{ name: 'forgot_password', query: this.$route.query }" @click.native="sendMetric('outlook_addin_home_forgot_password')" target="_blank"
                             class="forgot-pass">{{ $t('login.forgot_password') }}
                </router-link>
                <p>
                  {{ $t('login.no_account') }}
                  <router-link :to="{ name: 'register', query: this.$route.query }" @click.native="sendMetric('outlook_addin_home_signup')" target="_blank">
                    {{ $t('login.sign_up') }}
                  </router-link>
                </p>
              </div>
            </div>
            <div class="form-group">
              <div class="col-12 subtitle">
                <h5>{{ $t('msoffice.home_subtitle') }}</h5>
              </div>
              <div class="col-12 features">
                <p>
                  <i class='fas fa-check-circle' aria-hidden='true'></i>
                  <strong>{{ $t('msoffice.share_files') }}</strong> {{ $t('msoffice.share_files_from_relatico') }}
                </p>
                <p>
                  <i class='fas fa-check-circle' aria-hidden='true'></i>
                  <strong>{{ $t('msoffice.save_attach') }}</strong> {{ $t('msoffice.save_attach_from_contacts') }}
                </p>
                <p>
                  <i class='fas fa-check-circle' aria-hidden='true'></i>
                  <strong>{{ $t('msoffice.organize_files') }}</strong> {{ $t('msoffice.organize_files_via_tags') }}
                </p>
              </div>
            </div>
          </form>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import _loading from '../../components/_loading'

export default {
  name: "MsOfficeHome",
  components: {
    loading: _loading
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.axios.interceptors.request.use(
        config => {
          return vm.getIdToken().then((token) => {
            if(token)
              config.headers.common["Authorization"] = `Bearer ${token}`
            return config
          })
        },
        error => {
          return Promise.reject(error)
        }
      )
    })
  },
  data: () => {
    return {
      dialog: null,
      ready: false,
      loading: true,
      idToken: null
    }
  },
  watch: {
    ready(status) {
      if (status)
        this.startUp()
    },
    locale(){
      this.updateDisplayLocale()
    }
  },
  created(){
    if(this.isIE()){
      this.store.sendMetrics({name: 'outlook_addin_not_supported_from_IE', params: this.$route.params, path: this.$route.fullPath})
      this.$router.push({ name: 'unsupported_browser', query: this.$route.query })
    }
  },
  mounted() {
    this.officeStore.getOfficeApi().then(() => {
      this.ready = true
    }).catch(() => {
      this.store.sendMetrics({name: 'outlook_addin_not_supported_impossible_load', params: this.$route.params, path: this.$route.fullPath})
      this.loading = false
    })
  },
  computed: {
    office() {
      return window.Office
    },
    outlookSupported() {
      let isSupported = (
          this.host === this.office.HostType.Outlook &&
          this.office.context.requirements.isSetSupported("Mailbox") &&
          this.office.context.requirements.isSetSupported("DialogApi") &&
          this.office.context.requirements.isSetSupported("IdentityAPI")
      )

      if(!isSupported)
        this.store.sendMetrics({name: 'outlook_addin_not_supported_office_api', params: this.$route.params, path: this.$route.fullPath})

      return isSupported
    },
    displayLocale(){
      return this.office.context.displayLanguage.split('-')[0] || 'en'
    },
    host() {
      return this.office.context.host || null
    },
    mailbox(){
      return this.office.context.mailbox
    },
    diagnostcs() {
      return this.mailbox.diagnostics || {}
    },
    userAgent() {
      return navigator.userAgent
    },
    isRead(){
      const itemIsRead = this.mailbox.GetIsRead()
      const itemClass = this.mailbox.item.itemClass || null

      //GetIsRead() is from 1.5 and this support older apis
      if(itemIsRead === undefined){
        return itemClass === 'IPM.Note';
      }

      return itemIsRead
    },
    roaming() {
      return this.office.context.roamingSettings
    },
    ancestor(){
      return new URL(location.ancestorOrigins[0]) || null
    },
    allowedAncestors(){
      return process.env.VUE_APP_ALLOWED_IFRAME_ANCESTORS.split(' ') || null
    },
    isPersonalised() {
      if(this.ancestor && this.allowedAncestors)
        return !this.allowedAncestors.includes(this.ancestor.host)
      return false
    }
  },
  methods: {
    startUp() {
      this.updateDisplayLocale()
      this.store.sendMetrics({name: 'outlook_addin_open_home', params: this.$route.params, path: this.$route.fullPath})
      this.store.getCurrentUser().finally(() => {
        this.loading = false
      }).catch(() => {
        this.loading = false
      })
      this.getIdToken().then((token) => {
        this.idToken = token
      })
    },
    updateDisplayLocale(){
      if(this.locale !== this.displayLocale)
        this.$router.replace({query: {locale: this.displayLocale}})
    },
    openLogin() {
      let scope = this
      this.office.context.ui.displayDialogAsync(`${process.env.VUE_APP_BASE_URL}/msoffice/login?locale=${this.locale}`, {
        height: 70,
        width: 35,
        displayInIframe: false
      }, (result) => {
        if (result.status === scope.office.AsyncResultStatus.Succeeded) {
          scope.dialog = result.value
          scope.dialog.addEventHandler(scope.office.EventType.DialogMessageReceived, scope.processMessage)
          scope.dialog.messageChild(JSON.stringify({ success: true }))
          scope.saveStorage('id_token', this.idToken)
        } else {
          scope.removeStorage('id_token')
          scope.$toastr.error(scope.$t('msoffice.dialog_window_error'))
        }
      })
    },
    processMessage(arg = null) {
      if (arg && arg.message) {
        let message = JSON.parse(arg.message)

        if (message.status === 'success') {
          this.loading = true
          this.removeStorage('id_token')

          this.$toastr.success(this.$t('flash_messages.login_ok'))

          this.dialog.close()
          this.dialog = null

          this.store.sendMetrics({name: 'outlook_addin_login_successfull', params: message, path: this.$route.fullPath})

          if(this.isRead)
            this.$router.push({name: 'outlook_taskpane_reader', query: this.$route.query})
          else
            this.$router.push({name: 'outlook_taskpane_writer', query: this.$route.query})
        }else
          this.$toastr.error(this.$t('msoffice.dialog_window_error'))
      }
    },
    saveStorage(key, value) {
      let k = window.btoa('rel_'+key)

      window.localStorage.setItem(k, value)
      this.roaming.set(k, value)
      this.roaming.saveAsync()
    },
    removeStorage(key) {
      let k = window.btoa('rel_'+key)

      window.localStorage.removeItem(k)
      this.roaming.remove(k)
      this.roaming.saveAsync()
    },
    isIE() {
      return this.userAgent.indexOf("MSIE ") > -1 || this.userAgent.indexOf("Trident/") > -1
    },
    getIdToken() {
      let scope = this
      return new Promise((resolve, reject) => {
        scope.mailbox.getUserIdentityTokenAsync((result) => {
          if (result.status === "succeeded")
            resolve(window.btoa(result.value))
          else
            reject(result.error)
        })
      })
    },
    sendMetric(name){
      this.store.sendMetrics({name: name, params: this.$route.params, path: this.$route.fullPath})
    },
    openNewMessage(){
      this.mailbox.displayNewMessageForm({
        toRecipients: ['notifications@relatico.com'],
        subject: `${this.$t('msoffice.relatico_for_outlook')}`,
        htmlBody: `${this.$t('msoffice.request_allow_host')} "${this.ancestor.host}"`
      })
    }
  }
}
</script>

<style lang="sass" scoped>
@import "../../styles/account-pages.sass"
.login-btn
  margin-top: 10px
  min-width: 190px

.internal
  min-width: 90%
  form
    p
      font-size: 16px

.centered
  position: fixed
  top: 50%
  left: 50%
  transform: translate(-50%, -50%)
.loading-spinner
  margin: 0

.subtitle
  margin: 40px 0 20px 0

.wrapper-page .app-login form .features p
  margin-bottom: 20px
  font-size: 14px
  font-weight: 400
  color: #545454

  .fa-check-circle
    margin-right: 12px
    font-size: 14px
    font-weight: 900
    color: #667fff

.login-btn
  border: solid 1px #d2d2d2
  transition: border-color .25s ease-in-out, box-shadow .25s ease-in-out
  &:hover
    border: solid 1px #58a3da
    transition: border-color .25s ease-in-out, box-shadow .25s ease-in-out
</style>
